import React, { useMemo } from 'react'
import { Popconfirm, Typography } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { ButtonActionWrapper } from '../../../pages/list-users/styles'
import { Task, Sorter } from '../types'
import { formatPhone } from '../utils'
import { Value } from '../../elements'

interface UseTasksColumnsProps {
  sorter: Sorter
  stagesMap: Map<string, string>
  taskTypesMap: Map<string, string>
  isLeader: boolean
  onRemove: (id: number) => void
}

export const useTasksColumns = ({
  sorter,
  stagesMap,
  taskTypesMap,
  isLeader,
  onRemove,
}: UseTasksColumnsProps) => {
  return useMemo(() => {
    const beforeColumns: any[] = [
      {
        title: 'ID заказа/отклика',
        width: 80,
        dataIndex: 'card_id',
        key: 'card_id',
        sorter: true,
        sortOrder: sorter?.sort_field === 'id' ? sorter?.sort_order : null,
        render: (_, row) => {
          if (row?.card_type === 'measurements' && row?.case_id) {
            return row.case_id
          }
          return row.card_id
        },
      },
      {
        title: 'Стадия заказа/отклика',
        width: 150,
        dataIndex: 'parent_stage_name',
        key: 'parent_stage_name',
        sorter: true,
        sortOrder: sorter?.sort_field === 'parent_stage' ? sorter?.sort_order : null,
      },
      {
        title: 'Стадия',
        width: 130,
        dataIndex: 'stage',
        key: 'stage',
        sorter: true,
        sortOrder: sorter?.sort_field === 'stage' ? sorter?.sort_order : null,
        render: (stage: null | string) => {
          if (!stage) return null
          const label = stagesMap.get(stage) || stage
          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ padding: '2px 10px', borderRadius: 9 }}>{label}</div>
            </div>
          )
        },
      },
    ]

    const mainColumns = [
      {
        title: 'Тип',
        width: 120,
        dataIndex: 'task_type',
        key: 'task_type',
        render: val => taskTypesMap.get(val) || val,
        sorter: true,
        sortOrder: sorter?.sort_field === 'task_type' ? sorter?.sort_order : null,
      },
      {
        title: 'Комментарий',
        width: 150,
        dataIndex: 'task',
        key: 'task',
      },
      {
        title: 'Телефон',
        width: 150,
        dataIndex: 'phone',
        key: 'phone',
        render: val => formatPhone(val),
      },
      {
        title: 'Контактное лицо',
        width: 150,
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Дата',
        width: 250,
        dataIndex: 'deadline',
        key: 'deadline',
        render: (_, row) => (
          <dl className="attrs">
            <dt>Дата создания:</dt>
            <dd>{row.created_at ? dayjs(row.created_at).format('DD.MM.YYYY HH:mm') : ''}</dd>
            <dt>Плановая дата:</dt>
            <dd>{row.deadline ? dayjs(row.deadline).format('DD.MM.YYYY HH:mm') : ''}</dd>
            <dt>Дата завершения:</dt>
            <dd>
              {row.completed_at
                ? dayjs(row.completed_at).format('DD.MM.YYYY HH:mm')
                : '- не завершено -'}
            </dd>
          </dl>
        ),
        sorter: true,
        sortOrder: sorter?.sort_field === 'deadline' ? sorter?.sort_order : null,
      },
      {
        title: 'Причина завершения',
        width: 150,
        dataIndex: 'complete_reason',
        key: 'complete_reason',
        sorter: true,
        sortOrder: sorter?.sort_field === 'complete_reason' ? sorter?.sort_order : null,
      },
      {
        title: 'Автор',
        width: 200,
        dataIndex: 'author',
        key: 'author',
        sortOrder: sorter?.sort_field === 'author' ? sorter?.sort_order : null,
      },
      {
        title: 'Ответственные',
        width: 150,
        dataIndex: 'responsible',
        render: (val: null | string[]) => {
          if (!val) return val
          return val.map((item, index) => (
            <React.Fragment key={`responsible-${index}`}>
              {index === 0 ? (
                item
              ) : (
                <>
                  , <br />
                  {item}
                </>
              )}
            </React.Fragment>
          ))
        },
        key: 'responsible',
      },
    ]

    const actionColumn = [
      {
        title: 'Действия',
        dataIndex: 'operation',
        width: 80,
        align: 'center',
        style: { backgroundColor: '#FFF' },
        fixed: 'right',
        render: (_: any, record: Task) => {
          return (
            <span
              style={{ display: 'flex', justifyContent: 'center' }}
              onClick={e => {
                e.stopPropagation()
              }}
            >
              <Typography.Link>
                <ButtonActionWrapper title="Удалить">
                  <Popconfirm title="Удалить поле?" onConfirm={() => onRemove(record.id)}>
                    <DeleteOutlined />
                  </Popconfirm>
                </ButtonActionWrapper>
              </Typography.Link>
            </span>
          )
        },
      },
    ]

    const beforeLeaderColumns = [
      {
        title: 'Бренд',
        width: 150,
        dataIndex: 'brand',
        key: 'brand',
        sorter: true,
        render: val => <Value data={val} />,
      },
      {
        title: 'Офис',
        width: 200,
        dataIndex: 'office',
        key: 'office',
        sorter: true,
        render: val => <Value data={val} />,
      },
    ]

    return isLeader
      ? [...beforeColumns, ...beforeLeaderColumns, ...mainColumns, ...actionColumn]
      : [...beforeColumns, ...mainColumns]
  }, [sorter, stagesMap, taskTypesMap, isLeader, onRemove])
}
